import { useEffect, useMemo, useRef, useState } from 'react'
import { useUser } from '../../../../hooks/useUser'
import { hasUnreadProfileFeedback } from '../helpers/myProfileHelpers'
import { useMarkProfileFeedbackRead } from './useMarkFeedbackRead'

/** The My Profile boxes a player uncovers before their profile counts as read. */
export const PROFILE_FEEDBACK_BOX_IDS = {
  whatsWorking: 'whatsWorking',
  watchOut: 'watchOut'
} as const

export type ProfileFeedbackBoxId = (typeof PROFILE_FEEDBACK_BOX_IDS)[keyof typeof PROFILE_FEEDBACK_BOX_IDS]

type ProfileFeedbackCover = { isCovered: boolean; onRevealed: () => void }

/** Covers the shown boxes while the profile is unread, and marks it read once every one of them is uncovered. */
export const useProfileFeedbackCovers = (shownBoxIds: ProfileFeedbackBoxId[]) => {
  const { data: user } = useUser()
  const isUnread = hasUnreadProfileFeedback(user)
  const markRead = useMarkProfileFeedbackRead()
  const [uncoveredBoxIds, setUncoveredBoxIds] = useState<ProfileFeedbackBoxId[]>([])
  const hasMarkedReadRef = useRef(false)

  const hasUncoveredEveryBox = shownBoxIds.every((boxId) => uncoveredBoxIds.includes(boxId))
  const isReadNow = isUnread && shownBoxIds.length > 0 && hasUncoveredEveryBox

  useEffect(() => {
    if (!isReadNow || hasMarkedReadRef.current) return

    hasMarkedReadRef.current = true
    markRead()
  }, [isReadNow, markRead])

  return useMemo(() => {
    const coverFor = (boxId: ProfileFeedbackBoxId): ProfileFeedbackCover => ({
      isCovered: isUnread && !uncoveredBoxIds.includes(boxId),
      onRevealed: () =>
        setUncoveredBoxIds((uncovered) => (uncovered.includes(boxId) ? uncovered : [...uncovered, boxId]))
    })

    return {
      whatsWorking: coverFor(PROFILE_FEEDBACK_BOX_IDS.whatsWorking),
      watchOut: coverFor(PROFILE_FEEDBACK_BOX_IDS.watchOut)
    }
  }, [isUnread, uncoveredBoxIds])
}
